<template>
  <BasicCell
    :center
    :span
  >
    <BasicTooltip
      v-if="tooltip && !iconProps.disabled"
      :label="tooltip"
    >
      <BasicIcon
        v-bind="computedIconProps"
        @click="onClick"
      />
    </BasicTooltip>
    <BasicIcon
      v-else
      v-bind="computedIconProps"
      @click="onClick"
    />
  </BasicCell>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import BasicCell from './BasicCell.vue'
  import BasicTooltip from '@designSystem/components/basic/tooltip/BasicTooltip.vue'
  import BasicIcon from '@designSystem/components/basic/icon/BasicIcon.vue'
  import type { BasicCellActionIconProps } from './BasicCellActionIcon.types'
  import type { IconColor } from '../icon'

  const props = withDefaults(defineProps<BasicCellActionIconProps>(), {
    disabled: false,
    center: true,
    pointer: false,
    deactivated: false,
    isReadonly: false,
    iconProps: () => ({}),
  })

  const emit = defineEmits<{ (e: 'click'): void }>()

  const selectionnable = computed(() => !props.iconProps.disabled && !props.isReadonly)

  const iconColor = computed<IconColor>(() => {
    if (props.iconProps.disabled || props.deactivated) return 'neutral-300'
    if (props.iconName === 'trash') return 'danger-600'
    return 'primary-600'
  })

  const tooltip = computed(() => {
    if (!props.hoverMessages) return
    if (props.iconProps.disabled) return
    if (props.deactivated) return props.hoverMessages.inactif
    return props.hoverMessages.actif
  })

  const computedIconProps = computed(() => ({
    name: props.iconName,
    focusable: selectionnable.value,
    disabled: props.iconProps.disabled,
    pointer: selectionnable.value,
    readonly: props.isReadonly,
    color: iconColor.value,
    ...props.iconProps,
  }))

  const onClick = (e: MouseEvent) => {
    if (props.iconProps.disabled || props.isReadonly) return
    ;(e.currentTarget as HTMLElement)?.blur?.()
    emit('click')
  }
</script>

<style scoped lang="less">
  :deep(svg) {
    &:not(.icon--readonly, .icon--disabled):hover {
      filter: brightness(0.7);
      transition: filter 0.2s ease;
    }
  }
</style>
